import SearchDropdown from 'components/common/SearchDropdown'
import { CategoryTopic } from 'interface/CategoryTopic';
import { ReportFilterOptions } from 'interface/ReportFilterOptions';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/CategoryFilterCard.module.sass'
import { fetchReportCategoryFilters } from 'api/ReportApiService';
import { Option } from 'interface/Option';

const tag = 'CategoryFilterCard';

/**
 * Props for the CategoryFilterCard component.
 *
 * @param id - The unique identifier for the component.
 * @param reports - The list of reports to filter.
 * @param setFilteredReports - The function to update the filtered reports.
 */
interface CategoryFilterCardProps {
    id: string;
    reports: CategoryTopic[];
    setFilteredReports: React.Dispatch<React.SetStateAction<CategoryTopic[]>>;
}

/**
 * Selected filter criteria for reports.
 *
 * @param country - The selected country filter.
 * @param dataset - The selected dataset filter.
 * @param type - The selected report type filter.
 * @param format - The selected report format filter.
 */
interface Selected {
    country?: string | null;
    dataset?: string | null;
    type?: string | null;
    format?: string | null;
}

/**
 * Normalize a string by trimming whitespace and converting to lowercase.
 *
 * @param v - The string to normalize.
 * @returns The normalized string.
 */
const normalize = (v?: string | null) => (v ?? '').trim().toLowerCase()

/**
 * Check if a report field matches the selected filter criteria.
 *
 * @param reportValue - The value of the report field to match.
 * @param selected - The currently selected filter value.
 * @param selectedName - The name of the currently selected filter.
 * @returns True if the report field matches the selected filter, false otherwise.
 */
const matchField = (reportValue?: string, selected?: string | null, selectedName?: string | null) => {
    if (!selected && !selectedName) return true;
    const reportVal = normalize(reportValue);
    const sId = normalize(selected)
    const sName = normalize(selectedName)

    // Check if the report value matches the selected ID or name
    if (sId && reportVal === sId) return true;
    if (sName && reportVal === sName) return true;
    return false;
}

/**
 * Get the type of a report.
 *
 * @param report - The report to get the type from.
 * @returns The type of the report.
 */
const getReportType = (report: CategoryTopic): string => {
    return report.meta.type ?? 'Flagship'
}

/**
 * Get the format of a report.
 *
 * @param report - The report to get the format from.
 * @returns The format of the report.
 */
const getReportFormat = (report: CategoryTopic): string => {
    return report.meta.format ?? 'PDF'
}

/**
 * CategoryFilterCard component allows users to filter reports by various criteria.
 *
 * @param {CategoryFilterCardProps} props - The props for the component.
 * @returns {JSX.Element} The rendered CategoryFilterCard component.
 * 
 * @example
 * <CategoryFilterCard id="7a806589-677a-44d3-a3c8-4648fbfa5edc" reports={[]} setFilteredReports={() => {}} />
 */
const CategoryFilterCard: React.FC<CategoryFilterCardProps> = ({ id, reports, setFilteredReports }) => {
    const [resetToken, setResetToken] = useState<number>(0);
    const [options, setOptions] = useState<ReportFilterOptions>({
        datasetOptions: [],
        countryOptions: [],
        reportTypeOptions: [],
        reportFormatOptions: []
    })

    const [selected, setSelected] = useState<Selected>({
        country: null,
        dataset: null,
        type: null,
        format: null
    })

    const [selectedNames, setSelectedNames] = useState<Selected>({
        country: null,
        dataset: null,
        type: null,
        format: null
    })

    useEffect(() => {
        const retrieveFilterOptions = async () => {
            try {
                const filters = await fetchReportCategoryFilters({ tag, id });
                if (filters) {
                    setOptions({
                        datasetOptions: filters.datasetOptions,
                        countryOptions: filters.countryOptions,
                        reportTypeOptions: filters.reportTypeOptions,
                        reportFormatOptions: filters.reportFormatOptions
                    });
                }
            } catch (error) {
                console.error('Error fetching report filter options:', error);
            }
        }
        retrieveFilterOptions();
    }, [id])

    const filtered = useMemo(() => {
        const selCountry = selected.country;
        const selDataset = selected.dataset;
        const selType = selected.type;
        const selFormat = selected.format;

        const scName = selectedNames.country;
        const sdName = selectedNames.dataset;
        const stName = selectedNames.type;
        const sfName = selectedNames.format;

        if (!selCountry && !selDataset && !selType && !selFormat && !scName && !sdName && !stName && !sfName) {
            return reports;
        }


        return reports.filter(report => {
            const matchesCountry = matchField(report.country, selCountry,scName);
            const matchesDataset = matchField(report.dataset, selDataset, sdName);
            const matchesType = matchField(getReportType(report), selType, stName);
            const matchesFormat = matchField(getReportFormat(report), selFormat, sfName);
            return matchesCountry && matchesDataset && matchesType && matchesFormat;
        });
    }, [reports, selected, selectedNames]);

    useEffect(() => {
        setFilteredReports(filtered);
    }, [filtered]);


    const handleSelect = (key: keyof Selected, optionsArr: Option[]) => {
        return (idOrName: string) => {
            const selectedOption = optionsArr.find(option => option.id === idOrName || option.name === idOrName);
            if (selectedOption) {
                setSelected(prev => ({ ...prev, [key]: selectedOption.id ?? idOrName}));
                setSelectedNames(prev => ({ ...prev, [key]: selectedOption.name ?? idOrName }));
            }
        }
    }

    const resetFilters = useCallback(() => {
        setSelected({ country: null, dataset: null, type: null, format: null });
        setSelectedNames({ country: null, dataset: null, type: null, format: null });
        setFilteredReports(reports);
        setResetToken(prev => prev + 1);
    }, [reports, setFilteredReports]);

    return (
        <div className={styles['category-filter-card']}>
            <div className={styles['filter-header']}>
                <p className={styles['filter-title']}>Filter Categories</p>
                <button className={styles['reset-link']} onClick={resetFilters}>Reset Filters</button>
            </div>
            <div className={styles['filter-content']}>
                {options.countryOptions.length > 0 && (
                    <div className={styles['filter-item']}>
                        <p className={styles['filter-label']}>Country</p>
                        <SearchDropdown 
                            options={options.countryOptions} 
                            buttonName={''} 
                            placeholder={'Select a country...'} 
                            buttonColor={'transparent'} 
                            buttonContentColor='#005581'
                            onSelect={handleSelect('country', options.countryOptions)}
                            resetToken={resetToken}
                        />
                    </div>
                )}
                {options.datasetOptions.length > 0 && (
                    <div className={styles['filter-item']}>
                        <p className={styles['filter-label']}>Sub-Categories</p>
                        <SearchDropdown 
                            options={options.datasetOptions} 
                            buttonName={''} 
                            placeholder={'Select sub-categories...'} 
                            buttonColor={'transparent'} 
                            buttonContentColor='#005581'
                            onSelect={handleSelect('dataset', options.datasetOptions)}
                            resetToken={resetToken}
                        />
                    </div>
                )}
                {options.reportTypeOptions.length > 0 && (
                    <div className={styles['filter-item']}>
                        <p className={styles['filter-label']}>Report Type</p>
                        <SearchDropdown 
                            options={options.reportTypeOptions} 
                            buttonName={''} 
                            placeholder={'Select a type...'} 
                            buttonColor={'transparent'} 
                            buttonContentColor='#005581'
                            onSelect={handleSelect('type', options.reportTypeOptions)}
                            resetToken={resetToken}
                        />
                    </div>
                )}
                {options.reportFormatOptions.length > 0 && (
                    <div className={styles['filter-item']}>
                        <p className={styles['filter-label']}>Report Format</p>
                        <SearchDropdown
                            options={options.reportFormatOptions}
                            buttonName={''}
                            placeholder={'Select a file format...'}
                            buttonColor={'transparent'}
                            buttonContentColor='#005581'
                            onSelect={handleSelect('format', options.reportFormatOptions)}
                            resetToken={resetToken}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default CategoryFilterCard